import { ModuleAccessGuard } from "@/components/ModuleAccessGuard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building, Users, Home, Zap, FileText, ArrowRight, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TumCustomersList } from "./TumCustomersList";
import { TumPropertiesList } from "./TumPropertiesList";
import { TumTenantsList } from "./TumTenantsList";
import { TumUtilityAccountsList } from "./TumUtilityAccountsList";

const TumDashboard = () => {
  const navigate = useNavigate();

  return (
    <ModuleAccessGuard moduleId="tum">
      <div className="space-y-8 p-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tenant Utility Manager</h1>
          <p className="text-muted-foreground mt-2">
            Manage properties, tenants, and utility accounts in one place.
          </p>
        </div>

        {/* Quick Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Utility Bills
                  </CardTitle>
                  <CardDescription>
                    Upload, allocate, and manage bills
                  </CardDescription>
                </div>
                <Button onClick={() => navigate('bills')} size="sm">
                  View Bills
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
          </Card>

          <Card className="bg-primary/5 border-primary/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Tenant Billing
                  </CardTitle>
                  <CardDescription>
                    Track payments and statements
                  </CardDescription>
                </div>
                <Button onClick={() => navigate('billing')} size="sm">
                  Billing
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
          </Card>

          <Card className="bg-secondary/50 border-secondary/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Automation
                  </CardTitle>
                  <CardDescription>
                    Workflows and integrations
                  </CardDescription>
                </div>
                <Button onClick={() => navigate('automation')} size="sm" variant="secondary">
                  Automate
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
          </Card>

          <Card className="bg-accent/50 border-accent/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5" />
                    Analytics
                  </CardTitle>
                  <CardDescription>
                    Reports and insights
                  </CardDescription>
                </div>
                <Button onClick={() => navigate('analytics')} size="sm" variant="outline">
                  Analyze
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardHeader>
          </Card>
        </div>

        <Tabs defaultValue="properties" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="customers">
              <Building className="h-4 w-4 mr-2" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="properties">
              <Home className="h-4 w-4 mr-2" />
              Properties
            </TabsTrigger>
            <TabsTrigger value="tenants">
              <Users className="h-4 w-4 mr-2" />
              Tenants
            </TabsTrigger>
            <TabsTrigger value="accounts">
              <Zap className="h-4 w-4 mr-2" />
              Utility Accounts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="customers" className="mt-6">
            <TumCustomersList />
          </TabsContent>
          <TabsContent value="properties" className="mt-6">
            <TumPropertiesList />
          </TabsContent>
          <TabsContent value="tenants" className="mt-6">
            <TumTenantsList />
          </TabsContent>
          <TabsContent value="accounts" className="mt-6">
            <TumUtilityAccountsList />
          </TabsContent>
        </Tabs>
      </div>
    </ModuleAccessGuard>
  );
};

export default TumDashboard;
